import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  User, Save, ArrowLeft, Loader2, Upload, Link as LinkIcon,
  Linkedin, Check, X, Plus, Trash2, Lightbulb
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import ImpactCategorySelector from '../components/ImpactCategorySelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    linkedin_url: '',
    website_url: '',
    passion_areas: [],
    mission_statement: '',
    expertise: [],
    profile_picture_url: '',
    share_oi: false,
    share_msa: false,
    innovation_areas: []
  });

  const [newPassion, setNewPassion] = useState('');
  const [newExpertise, setNewExpertise] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        bio: user.bio || '',
        linkedin_url: user.linkedin_url || '',
        website_url: user.website_url || '',
        passion_areas: user.passion_areas || [],
        mission_statement: user.mission_statement || '',
        expertise: user.expertise || [],
        profile_picture_url: user.profile_picture_url || '',
        share_oi: user.share_oi || false,
        share_msa: user.share_msa || false,
        innovation_areas: user.innovation_areas || []
      });
    }
  }, [user]);

  const checkUsername = async (username) => {
    if (!username || username === user?.username) {
      setUsernameAvailable(null);
      return;
    }

    try {
      setCheckingUsername(true);
      const response = await axios.post(`${BACKEND_URL}/api/profile/check-username`, {
        username
      });
      setUsernameAvailable(response.data.available);
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameAvailable(false);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, username: value });
    
    // Debounce username check
    clearTimeout(window.usernameTimeout);
    window.usernameTimeout = setTimeout(() => checkUsername(value), 500);
  };

  const addPassionArea = () => {
    if (newPassion.trim() && !formData.passion_areas.includes(newPassion.trim())) {
      setFormData({
        ...formData,
        passion_areas: [...formData.passion_areas, newPassion.trim()]
      });
      setNewPassion('');
    }
  };

  const removePassionArea = (index) => {
    setFormData({
      ...formData,
      passion_areas: formData.passion_areas.filter((_, i) => i !== index)
    });
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !formData.expertise.includes(newExpertise.trim())) {
      setFormData({
        ...formData,
        expertise: [...formData.expertise, newExpertise.trim()]
      });
      setNewExpertise('');
    }
  };

  const removeExpertise = (index) => {
    setFormData({
      ...formData,
      expertise: formData.expertise.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.username && usernameAvailable === false) {
      toast({
        title: "Username not available",
        description: "Please choose a different username",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      
      // Get auth token for Authorization header
      const token = localStorage.getItem('if_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${BACKEND_URL}/api/profile/edit`, formData, {
        headers,
        withCredentials: true
      });
      
      toast({
        title: "Profile updated!",
        description: "Your Fountainhead profile has been updated successfully"
      });
      
      // Refresh user data
      await refreshUser();
      
      // Navigate to public profile
      if (formData.username) {
        navigate(`/profile/${formData.username}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error updating profile",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Edit Profile
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <form onSubmit={handleSubmit}>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Username */}
              <div>
                <Label htmlFor="username">Username / Handle *</Label>
                <div className="relative">
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={handleUsernameChange}
                    placeholder="yourhandle"
                    className="pr-10"
                    required
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingUsername && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                    {!checkingUsername && usernameAvailable === true && <Check className="w-4 h-4 text-green-600" />}
                    {!checkingUsername && usernameAvailable === false && <X className="w-4 h-4 text-red-600" />}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">3-30 characters, letters, numbers, underscore, or hyphen</p>
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell other Fountainheads about yourself..."
                  rows={4}
                />
              </div>

              {/* Mission Statement */}
              <div>
                <Label htmlFor="mission">Mission Statement</Label>
                <Textarea
                  id="mission"
                  value={formData.mission_statement}
                  onChange={(e) => setFormData({ ...formData, mission_statement: e.target.value })}
                  placeholder="What drives your innovation and creativity?"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5" />
                Links & Social
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="linkedin">
                  <Linkedin className="w-4 h-4 inline mr-2" />
                  LinkedIn URL
                </Label>
                <Input
                  id="linkedin"
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div>
                <Label htmlFor="website">Website URL</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div>
                <Label htmlFor="profile_pic">Profile Picture URL</Label>
                <Input
                  id="profile_pic"
                  type="url"
                  value={formData.profile_picture_url}
                  onChange={(e) => setFormData({ ...formData, profile_picture_url: e.target.value })}
                  placeholder="https://example.com/your-photo.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">Or upload a file (coming soon)</p>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Expertise & Interests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Expertise */}
              <div>
                <Label>Expertise Areas</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    placeholder="e.g., AI, Design, Research"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
                  />
                  <Button type="button" onClick={addExpertise} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.expertise.map((item, index) => (
                    <span key={index} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removeExpertise(index)} className="hover:text-purple-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Passion Areas */}
              <div>
                <Label>Passion Areas</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newPassion}
                    onChange={(e) => setNewPassion(e.target.value)}
                    placeholder="e.g., Sustainability, Education"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPassionArea())}
                  />
                  <Button type="button" onClick={addPassionArea} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.passion_areas.map((item, index) => (
                    <span key={index} className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removePassionArea(index)} className="hover:text-pink-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Innovation Areas Section - NEW */}
          <Card className="mb-8 border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-pink-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-purple-600" />
                My Innovation Areas
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                What impact areas are you passionate about innovating in? This helps showcase 
                platform impact without us accessing your actual ideas.
              </p>
            </CardHeader>
            <CardContent>
              <ImpactCategorySelector
                value={formData.innovation_areas}
                onChange={(areas) => setFormData({ ...formData, innovation_areas: areas })}
                maxSelections={5}
                mode="multi"
                showDescription={false}
                collapsible={false}
                placeholder="Search impact areas or add your own..."
              />
            </CardContent>
          </Card>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Privacy Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Share Originality Index (OI) publicly</Label>
                  <p className="text-sm text-gray-500">Allow others to see your OI scores</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.share_oi}
                  onChange={(e) => setFormData({ ...formData, share_oi: e.target.checked })}
                  className="w-5 h-5"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Share Mirror-Spark Assessment (MSA) publicly</Label>
                  <p className="text-sm text-gray-500">Allow others to see your Spark %</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.share_msa}
                  onChange={(e) => setFormData({ ...formData, share_msa: e.target.checked })}
                  className="w-5 h-5"
                />
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-900">
                  <strong>Note:</strong> Your IQI (Interaction Quality Index) is always public to maintain quality interactions in the Fountainhead community.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading || (usernameAvailable === false)}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
